import React from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'
import { Box, Highlight, Icon } from '@node-space/ui-components'
import { ConditionalTooltip } from 'components/Conditional/ConditionalTooltip'
import { PathNames } from 'constants/General'
import { formatAmount } from 'pages/Rewards/utils'
import { Card, CardStatus } from 'services/CardService'

interface Wallet {
  id: string
  customerReference: string
  name: string
  balance: {
    value: number | string
    currencyCode: string
  }
}

interface CardDetailsInfoProps {
  card: Card
  wallet: Wallet
}

const getStatusHighlightProps = (status: CardStatus) => {
  switch (status) {
    case 'ACTIVE':
      return { type: 'success' as const, labelKey: 'customers.cardDetails.statusActive' }
    case 'FROZEN':
      return { type: 'warning' as const, labelKey: 'customers.cardDetails.statusFrozen' }
    case 'BLOCKED':
      return { type: 'error' as const, labelKey: 'customers.cardDetails.statusBlocked' }
    case 'CANCELLED':
      return { type: 'neutral' as const, labelKey: 'customers.cardDetails.statusCancelled' }
    default:
      return { type: 'neutral' as const, labelKey: 'customers.cardDetails.statusUnknown' }
  }
}

export const CardDetailsInfo: React.FC<CardDetailsInfoProps> = ({ card, wallet }) => {
  const { t } = useTranslation()
  const statusProps = getStatusHighlightProps(card.status)

  return (
    <Box flexCol gapY={4} paddingY={4}>
      <Box tag="p" className="text-h4 text-foreground-prominent">
        ****{card.last4Digits}{' '}
        <span className="text-foreground-faint">{t('customers.cardDetails.linkedToWallet')} </span>
        <Link
          to={generatePath(PathNames.CUSTOMER_WALLET, {
            customerReference: wallet.customerReference,
            customerWalletId: wallet.id,
          })}
          className="hover:underline"
        >
          {wallet.name}
        </Link>
      </Box>

      <Box tag="h2" className="text-h2 text-foreground-prominent">
        {formatAmount(Number(wallet.balance.value))} {wallet.balance.currencyCode}
      </Box>

      <Box paddingT={4}>
        <ConditionalTooltip
          isEnabled={card.status === 'BLOCKED'}
          bodyContent={t('customers.cardDetails.statusBlockedTooltip')}
          type="simple"
          position="bottom"
          triggerAsChild={false}
        >
          <Highlight
            iconElement={<Icon name="CheckCircle" size="small" />}
            variant="status"
            type={statusProps.type}
          >
            {t(statusProps.labelKey)}
          </Highlight>
        </ConditionalTooltip>
      </Box>
    </Box>
  )
}
